/**
 * Utility functions for formatting addresses
 * Consistently removes country codes from the end of addresses
 */

/**
 * Remove country from the end of an address string
 * Handles both generic country patterns and specific country names
 * 
 * @param address - The address string to format
 * @returns The address with country code removed
 * 
 * @example
 * removeCountryFromAddress("123 Main St, New York, United States")
 * // Returns: "123 Main St, New York"
 * 
 * removeCountryFromAddress("456 Oak Ave, Toronto, Canada")
 * // Returns: "456 Oak Ave, Toronto"
 */
export const removeCountryFromAddress = (address: string): string => {
  if (!address) return '';
  
  // First try specific country patterns for more precise matching
  let result = address
    .replace(/,\s*USA\s*$/i, '')
    .replace(/,\s*United States\s*$/i, '')
    .replace(/,\s*United States of America\s*$/i, '')
    .replace(/,\s*Canada\s*$/i, '')
    .replace(/,\s*Mexico\s*$/i, '')
    .trim();
  
  // If no specific pattern matched, try generic pattern
  // This catches any remaining country names at the end
  if (result === address.trim()) {
    result = address.replace(/,\s+[A-Za-z\s]+$/, '').trim();
  }
  
  return result;
};

/**
 * Alias for backwards compatibility
 * @deprecated Use removeCountryFromAddress instead
 */
export const formatAddress = removeCountryFromAddress;

/**
 * Alias for backwards compatibility
 * @deprecated Use removeCountryFromAddress instead
 */
export const removeCountry = removeCountryFromAddress;

